import { TextInput, TextProps } from '../index';
import React, { forwardRef, ReactNode, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import Inputmask from 'inputmask';
import { DateDetails } from '../../models/DateDetails';
import { DateTime } from 'luxon';

interface DateMaskProps extends TextProps {
    min?: DateTime;
    max?: DateTime;
    maxDetails?: DateDetails;
    time?: boolean;
    children?: ReactNode;
    disableDateRangeValidation?: boolean;
    dateHasBeenChangedAlgorithmically?: boolean;
    setDateHasBeenChangedAlgorithmically?: (value: boolean) => void;
}

export const DateMask = forwardRef<HTMLInputElement, DateMaskProps>((props, ref) => {
    const dateInputRef = useRef<HTMLInputElement>(null);

    const latestProps = useRef(props);
    latestProps.current = props;

    // --- compute min/max strings ---
    const min = useMemo(() => {
        if (!props.min) return undefined;
        return props.time ? props.min.toFormat('dd-MM-yyyy HH:mm') : props.min.toFormat('dd-MM-yyyy');
    }, [props.min, props.time]);

    const max = useMemo(() => {
        if (!props.max) return undefined;
        return props.time ? props.max.toFormat('dd-MM-yyyy HH:mm') : props.max.toFormat('dd-MM-yyyy');
    }, [props.max, props.time]);

    // --- mask format ---
    const inputFormat =
        props.maxDetails === DateDetails.Decade
            ? 'yyyy'
            : props.maxDetails === DateDetails.Year
            ? 'mm-yyyy'
            : props.time
            ? 'dd-mm-yyyy HH:MM'
            : 'dd-mm-yyyy';

    // --- prevent DOM mutation during render ---
    useEffect(() => {
        if (!dateInputRef.current) return;

        const im = new Inputmask({
            alias: 'datetime',
            inputFormat,
            placeholder: inputFormat.toLowerCase(),
            min: props.disableDateRangeValidation ? undefined : min,
            max: props.disableDateRangeValidation ? undefined : max,
            insertMode: false,
            insertModeVisual: true,
            shiftPositions: false,
            nullable: true,
            prefillYear: false,
            clearMaskOnLostFocus: true,
            showMaskOnHover: false,
            preValidation(buffer, pos, char: any, isSelection, opts, maskset, caretPos, strict) {
                if (strict) return true;
                if (isNaN(char)) return false;
                return true;
            },
            oncleared() {
                if (!dateInputRef.current) return;

                const len = latestProps.current.value?.length ?? 0;
                const start = dateInputRef.current.selectionStart;
                const end = dateInputRef.current.selectionEnd;

                if (start === len && end === len) {
                    latestProps.current.onChange?.('');
                    dateInputRef.current.setSelectionRange(0, 0);
                }
            },
        });

        im.mask(dateInputRef.current);
        const input = dateInputRef.current;
        const onFocus = () => {
            requestAnimationFrame(() => input.setSelectionRange(0, input.value.length));
        };
        input.addEventListener('focus', onFocus);

        return () => {
            input.removeEventListener('focus', onFocus);
            // cleanup mask
            if (dateInputRef.current) {
                try {
                    im.remove();
                } catch {}
            }
        };
    }, [inputFormat, min, max, props.disableDateRangeValidation]);

    // --- React 19 safe ref exposure ---
    useImperativeHandle(ref, () => dateInputRef.current!);

    return <TextInput {...props} ref={dateInputRef} />;
});
